import React, { useState } from 'react';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { HandoffLog } from './handoff-log';
import {
  Handoff,
  HandoffCitations,
  HandoffStatus,
} from '../../interfaces/handoff-interfaces';
import { IqSection, IqStatus, STATUS_LABEL } from '../../interfaces/iq-interfaces';
import {
  buildingWindowLabel,
  clockDayTime,
  clockTime,
  elapsed,
} from '../../function-utils/building-time';
import {
  IqCard,
  AuroraHeader,
  Spark,
  CardBody,
  Headline,
  SectionRow,
  RowHead,
  RowText,
  SourceRow,
  Caption,
  GhostButton,
} from './iq-shared.styled';
import {
  SubHead,
  CustodyBar,
  CustodyLine,
  CustodyMeta,
  MetaMono,
  Person,
  UnknownPerson,
  NoticeBand,
  BandTitle,
  SourceRef,
} from './iq-handoff.styled';

/**
 * ONE HANDOVER RECORD, rendered as the frozen thing it is.
 *
 * ═══════ WHAT THIS CARD MUST NOT OFFER, AND WHY IT IS NOT AN OVERSIGHT ═══════
 * There is no "Generate", no "Regenerate anyway", no "Try again" — every one of
 * which IqSummaryCard offers, correctly, for the on-demand summary. D-25: "the
 * summary is generated ONCE at handoff and PERSISTED, not regenerated on read. A
 * handover that changes its wording each time it is opened is not a record."
 * A retry button here would rewrite history and bill a second generation for the
 * privilege. If the wording is a floor, the floor is what was handed over, and
 * the card says so instead of offering to change it.
 *
 * The citations are likewise references, not jump targets: the server does not
 * re-fetch the window on read, so there is no log on this screen to scroll to.
 * They are still LABELLED rather than printed raw — "not clickable" was never a
 * decision to show a reader a database key. See REF_LABEL below.
 *
 * ═══════════════════ THE ORDER OF THE CARD IS THE ARGUMENT ══════════════════
 *   1. WHO handed to WHOM                — the chain of custody
 *   2. WHEN, and for WHICH window        — with the timezone stated, because a
 *                                          shift boundary read in the wrong zone
 *                                          is a wrong shift
 *   3. Anything that makes this NOT a clean handover  — before the prose
 *   4. The summary
 *
 * Point 3 is D-25 in as many words: "a shift that was never clocked out — the
 * handover has to say so rather than pretend a clean handover happened."
 */

/* Takes an OPTIONAL status for the same reason SectionList reads every field
   defensively: this renders frozen JSON that may predate the current vocabulary,
   and an unknown status must produce a neutral pill rather than a crash. */
const toneFor = (s?: IqStatus): 'success' | 'warning' | 'alert' =>
  s === 'incident' ? 'alert' : s === 'follow_up' ? 'warning' : 'success';

/* The words for `degraded_reason`. The server sends a machine token and the FE
 * owns the sentence — the same split iq-summary-card documents at §6.10.
 *
 * EVERY SENTENCE IS PAST TENSE AND NONE OFFERS A RETRY. This is a record of what
 * was handed over, so the reader needs to know what the incoming person actually
 * received, not what they might get if they pressed something now. */
const DEGRADED_SENTENCE: Record<string, string> = {
  pending:
    'The written summary never finished — the handover was recorded before it completed. Everything below is straight from the log.',
  empty: 'Nothing at all was logged during this shift.',
  quiet: 'A quiet shift. What was logged is listed below.',
  source_truncated:
    'This shift was too long to summarise in one pass, so no written summary was produced. What is below comes straight from the log, and it does not cover the whole window — open the log for the rest.',
  budget_exceeded:
    'Lobbero IQ had reached this month’s limit for your account when this shift ended, so no written summary was produced. The facts below come straight from the log.',
  not_entitled:
    'Lobbero IQ was not active on this account when this shift ended, so no written summary was produced. The facts below come straight from the log.',
  busy: 'The desk was generating several summaries at once, so this one was not written. The facts below come straight from the log.',
  timeout:
    'The written summary took too long and was abandoned. The facts below come straight from the log.',
  vendor_error:
    'The summary service was unavailable when this shift ended. The facts below come straight from the log.',
  rate_limited:
    'Too many summaries were requested at once, so this one was not written. The facts below come straight from the log.',
  quota_daily:
    'The day’s summary allowance was already spent when this shift ended. The facts below come straight from the log.',
  invalid_json:
    'The written summary came back incomplete and was discarded. The facts below come straight from the log.',
  citation_failed:
    'The written summary could not be traced back to the log, so it was discarded. The facts below come straight from the log.',
  refused:
    'No written summary could be produced for this shift. The facts below come straight from the log.',
  safety_blocked:
    'The content filter would not summarise this shift — usually because entries describe a serious incident or carry personal details. Nothing was lost: the facts below come straight from the log.',
  spii_blocked:
    'The content filter would not summarise this shift — usually because entries describe a serious incident or carry personal details. Nothing was lost: the facts below come straight from the log.',
  unclassified:
    'The written summary could not be produced. The facts below come straight from the log.',
};

/* THE PILL ANSWERS ONE QUESTION: did a model write this, or is it the server's
 * own deterministic account of the shift?
 *
 * That is the only distinction worth a badge here, and it is the one the aurora
 * gradient encodes everywhere else in the product. `partial` still carries "✦ AI"
 * because a model DID write it — the fact that the incident floor added a line
 * the model missed is a separate warning tag beside it, not a different kind of
 * document. `stale` overwrites the previous status when the token map is erased,
 * so it names the erasure rather than the authorship. */
const STATUS_PILL: Record<
  HandoffStatus,
  { tone: 'brand' | 'neutral'; text: string }
> = {
  ok: { tone: 'brand', text: '✦ AI' },
  partial: { tone: 'brand', text: '✦ AI' },
  floor: { tone: 'neutral', text: 'Straight from the log' },
  stale: { tone: 'neutral', text: 'Names erased' },
};

/* ═════════════ WHERE THE HANDOVER GOT TO — SAID ONCE, IN ONE PLACE ═════════
 *
 * This card used to say it three times in three voices: a tag reading "Waiting
 * for the next shift", a custody sentence ending "Nobody has clocked in to take
 * it yet", and a meta note reading "· nobody has taken it over yet". All three
 * rendered on the same condition, so a reader met what looked like three
 * separate states and had to work out they were one.
 *
 * It is ONE tag now, and it is the only place the state is named. The custody
 * sentence says WHO, the meta line says WHEN, and neither repeats this. */
// Exported for the gate's summary panel (handoff-gate-panel.tsx), so the two
// surfaces cannot name the same state in two different words.
export const custodyStateOf = (
  h: Handoff
): { tone: 'neutral' | 'warning' | 'success'; text: string } =>
  // PENDING FIRST: it is the state where there is no recipient at all, so
  // "Unread" would be an accusation aimed at nobody. Migration 076 publishes a
  // handover to the DESK and names the recipient only when somebody clocks in.
  h.pending
    ? { tone: 'neutral', text: 'Waiting for the next shift' }
    : h.read_at
    ? { tone: 'success', text: 'Taken over' }
    : { tone: 'warning', text: 'Unread' };

/** "Thu 06 Mar 22:00 – Fri 07 Mar 06:04 · Building time — Sofia".
 *
 *  THE CLOCK IS PART OF THE ANSWER, not a footnote. A handover is read by the
 *  person taking the next shift at a specific desk; the window rendered in the
 *  reader's own zone would silently relabel a night shift. So the building's
 *  clock is used and NAMED — for an owner reading it from another city too
 *  (R-T12: a handover is a building record). An unusable zone does not blank
 *  the record: the one time module falls back and the note says so. */
const fmtWindow = (from: string, to: string, tz: string): string =>
  buildingWindowLabel(from, to, tz, { withDate: true });

const NameOrUnknown: React.FC<{ name: string; fallback: string }> = ({
  name,
  fallback,
}) =>
  name ? (
    <Person>{name}</Person>
  ) : (
    <UnknownPerson title="This account no longer exists, or the name could not be read.">
      {fallback}
    </UnknownPerson>
  );

/* ═══════════════ A REFERENCE IS A LABEL, NEVER A KEY (RC-12) ═══════════════
 *
 * `source_ids` are the tokens the summary was FROZEN with — 'v:24', 'd:412',
 * 'pa:<uuid>' — and this card used to print them verbatim, so a handover ended
 * with a row of database keys under the prose. The written decision that these
 * are "NOT a button" was about CLICKABILITY (the snapshot ships no entries, so
 * there is nothing on this screen to scroll to); it was never a decision to show
 * the reader a uuid.
 *
 * The prefix is the whole of the information the FROZEN row carries, so the
 * prefix is what is named. The raw key survives on `title` — hover, for whoever
 * is chasing a row in support — and nowhere else: not as text, not as an
 * aria-label, not as a fallback. */
const REF_LABEL: Record<string, string> = {
  v: 'Visitor entry',
  d: 'Log entry',
  pa: 'Parcel in',
  ph: 'Parcel out',
  // A parcel's OTHER departure (backend migration 085): it went back to the
  // carrier rather than to a person. Labelled distinctly here because a frozen
  // citation is the one place a reader cannot click through to find out which.
  pr: 'Parcel returned',
};

/* The same four things said in the wire's vocabulary. Kept as a SEPARATE map
 * rather than translated into prefixes, so a `kind` the server adds later lands
 * on the prefix fallback instead of on a wrong label. */
const CITATION_LABEL: Record<string, string> = {
  visitor: 'Visitor entry',
  log: 'Log entry',
  parcel_in: 'Parcel in',
  parcel_out: 'Parcel out',
};

/** An id whose prefix means nothing here — a token shape from a later server,
 *  or prose that reached `source_ids`. "Related record" is true of all of them
 *  and the raw string is true of none of them for a reader. */
const UNKNOWN_REF = 'Related record';
/** Nothing to point at at all: an empty or whitespace-only entry. Rendered
 *  rather than dropped, because a section that cited SOMETHING and lost it is a
 *  different fact from a section that cited nothing. */
const MISSING_REF = 'Related record unavailable';

/** WHAT THIS REFERENCE WAS, in words.
 *
 *  With a citation (WP-10 step 2) the server has resolved the frozen token
 *  inside the request and the label gains the time it happened. Without one —
 *  an older server, an erased row, a log entry since deleted — the prefix alone
 *  still answers "what kind of thing was this", which is the part the reader
 *  actually needs. The snapshot is untouched either way: this resolves at READ
 *  time and never writes back. */
const refLabel = (
  id: string,
  tz: string,
  citations?: HandoffCitations
): string => {
  const token = (id || '').trim();
  if (!token) return MISSING_REF;

  const cut = token.indexOf(':');
  const base = (cut > 0 ? REF_LABEL[token.slice(0, cut)] : '') || UNKNOWN_REF;

  const cite = citations ? citations[token] : undefined;
  if (!cite) return base;

  // The KIND wins over the prefix when the server resolved one — it read the
  // row, the prefix is only a naming convention — but an unrecognised kind falls
  // back to the prefix rather than to nothing.
  const label = CITATION_LABEL[cite.kind] || base;
  // The window's zone, never the reader's, for the same reason fmtWindow uses
  // it: a handover is read at a desk. An unparseable `at` yields '' and the
  // label is printed on its own rather than trailing a naked separator.
  const at = clockTime(cite.at, tz);

  return at ? `${label} · ${at}` : label;
};

/**
 * ── A FROZEN RECORD CAN OUTLIVE THE SHAPE THAT WROTE IT ──
 *
 * `summary_output` is a snapshot: it is written once and never rewritten (D-25 —
 * "a handover that changes its wording each time it is opened is not a record"),
 * so this component is guaranteed, over time, to be handed JSON produced by a
 * prompt version that no longer exists. This code read `s.source_ids.length`
 * straight, and a single section without that array threw
 * "Cannot read properties of undefined (reading 'length')" — which React turns
 * into an unmounted tree, i.e. A BLANK SCREEN.
 *
 * That was survivable while the card only appeared inside the IQ chat, which the
 * reader had to open. It is not survivable now: the card renders on the
 * pre-clock-in gate, which is the FIRST screen every desk worker sees and the
 * one that has to work when nothing else does. One malformed row would white-
 * screen the whole product for that person, and the thing they could not then
 * reach is the clock-in button.
 *
 * So every field is read defensively and an unrecognised status degrades to a
 * neutral pill rather than to `undefined` in a lookup table. Nothing is hidden:
 * a section with no text still prints its label, and a section with nothing at
 * all still occupies a row, which is the honest rendering of a row that exists
 * and says nothing.
 */
const SectionList: React.FC<{
  sections: IqSection[];
  tz: string;
  citations?: HandoffCitations;
}> = ({ sections, tz, citations }) => (
  <>
    {(sections || []).map((s, i) => (
      <SectionRow key={`${s?.status || 'section'}-${i}`}>
        <RowHead>
          <Pill tone={toneFor(s?.status)}>
            {STATUS_LABEL[s?.status] || 'Noted'}
          </Pill>
          {s?.label ? <Tag tone="neutral">{s.label}</Tag> : null}
          {s?.system ? <Tag tone="neutral">Recorded automatically</Tag> : null}
        </RowHead>
        {s?.text ? <RowText>{s.text}</RowText> : null}
        {(s?.source_ids || []).length ? (
          <SourceRow>
            {s.source_ids.map(id => (
              <SourceRef key={id} title={id}>
                {refLabel(id, tz, citations)}
              </SourceRef>
            ))}
          </SourceRow>
        ) : null}
      </SectionRow>
    ))}
  </>
);

interface Props {
  handoff: Handoff;
  /** How the reader relates to this record. It changes the sentence, not the
   *  data: "Aurora Desk handed this shift to you" and "You handed this shift to
   *  Aurora Desk Two" are the same row read from two ends, and a card that said
   *  "handed to you" on a manager's audit screen would be lying. */
  viewer: 'recipient' | 'outgoing' | 'observer';
  /** A FALLBACK, for a record whose own `building_name` came back empty — not an
   *  override. It used to win, and callers pass it from whatever building their
   *  shell has selected, so a manager reading yesterday's handover from another
   *  desk was shown today's building name. See the note on the meta row below. */
  buildingName?: string;
  /** Set by hosts that already draw a bordered card around this one, so the
   *  record does not arrive double-framed. See IqCard's $flush note. */
  flush?: boolean;
}

/** The gap between two stamps, as a phrase rather than a bare number.
 *
 *  `elapsed` answers "just now" under a minute and a duration above it, so a
 *  caller that appends "later" to every answer prints "just now later". This
 *  owns the whole phrase instead, which is the only way the two branches can
 *  both read as English. */
const gap = (fromIso: string, toIso: string): string => {
  const d = elapsed(fromIso, toIso);

  return d === 'just now' ? d : `${d} later`;
};

export const IqHandoffCard: React.FC<Props> = ({
  handoff,
  viewer,
  buildingName,
  flush,
}) => {
  const h = handoff;
  const reason = h.degraded_reason || '';
  const isFloor = h.status === 'floor';
  const sections = h.summary?.sections || [];
  const headline = h.summary?.headline || '';
  const statusPill = STATUS_PILL[h.status];
  const custodyState = custodyStateOf(h);
  // The IQ card's "View the full log" — closed until asked for, per card.
  const [logOpen, setLogOpen] = useState(false);
  // The server's own answer to "may this reader see real names from the log":
  // it holds the log-read pair, which is exactly what the log route requires.
  const canSeeLog = !!h.names_visible;

  // The clamp section the SERVER authored is already inside `sections` (system:
  // true, no citations). The band below repeats the fact in the FE's own words
  // ABOVE the summary rather than replacing it, because the two are doing
  // different jobs: the band is the warning the reader must not scroll past, the
  // section is the sentence that belongs inside the record for ever.
  const clamped = !!h.window?.clamped;

  const custody =
    viewer === 'recipient' ? (
      <CustodyLine>
        <NameOrUnknown
          name={h.outgoing_user_name}
          fallback="Someone who no longer has an account"
        />
        {' handed this shift to you.'}
      </CustodyLine>
    ) : viewer === 'outgoing' ? (
      // PENDING IS CHECKED FIRST, and it is the COMMON case for this viewer:
      // the receipt opens the instant a handover is written, when by definition
      // nobody has clocked in to take it. Without this branch that receipt read
      // "You handed this shift to someone who no longer has an account" — the
      // null recipient falling through to a fallback about DELETED users, so
      // the screen accused the desk of handing over to a ghost every single
      // time. Same reasoning as the observer branch below.
      //
      // The sentence stops at WHO. That nobody has taken it yet is the STATUS,
      // and the status tag in the header is where it is said.
      h.pending ? (
        <CustodyLine>{'You handed this shift over.'}</CustodyLine>
      ) : (
        <CustodyLine>
          {'You handed this shift to '}
          <NameOrUnknown
            name={h.recipient_user_name}
            fallback="someone who no longer has an account"
          />
          {'.'}
        </CustodyLine>
      )
    ) : h.pending ? (
      // NOT YET CLAIMED (backend migration 076). A handover is published to the
      // DESK on clock-out and named only when somebody clocks in to take it, so
      // there is genuinely no second party yet. The other branches' fallback —
      // "someone who no longer has an account" — would report a deleted user
      // where the truth is that the next shift has not started. Stating that is
      // the status tag's job, not this sentence's.
      <CustodyLine>
        <NameOrUnknown
          name={h.outgoing_user_name}
          fallback="Someone who no longer has an account"
        />
        {' handed this shift over.'}
      </CustodyLine>
    ) : (
      <CustodyLine>
        <NameOrUnknown
          name={h.outgoing_user_name}
          fallback="Someone who no longer has an account"
        />
        {' handed this shift to '}
        <NameOrUnknown
          name={h.recipient_user_name}
          fallback="someone who no longer has an account"
        />
        {'.'}
      </CustodyLine>
    );

  return (
    <IqCard $flush={flush}>
      {/* AURORA — and only here. This header marks the SUMMARY as model-written;
          everything under CustodyBar is a database row.

          THE SPARK IS DROPPED WHEN THE SUMMARY IS THE DETERMINISTIC FLOOR.
          `status === 'floor'` means no model wrote a word of this — the record
          was assembled from the log because generation was unavailable or the
          account never had Lobbero IQ at all. The aurora is reserved for the AI
          and this card now renders on the pre-clock-in gate, which every desk
          worker sees whether or not the account bought IQ; marking a
          hand-assembled record with it would advertise a feature that is not
          there and take credit for work the model did not do. */}
      <AuroraHeader>
        {h.status === 'floor' ? null : <Spark>✦</Spark>}
        Shift handover
        {/* THE ONE STATUS LINE. Same component and the same tone vocabulary as
            the section states below it — "Follow up", "Still inside",
            "Recorded automatically", "All clear" — so every state on this card
            is read the same way, and the words carry the meaning rather than
            the colour (design_system §7). */}
        <Tag tone={custodyState.tone}>{custodyState.text}</Tag>
      </AuroraHeader>

      {/* NO AURORA below this line. Who, when, which window — hard record. */}
      <CustodyBar>
        {custody}
        <CustodyMeta>
          {h.outgoing_user_role_label ? (
            <Tag tone="neutral">{h.outgoing_user_role_label}</Tag>
          ) : null}
          {/* WHICH DESK AND WHICH PERSON, always, for every reader.
              The role tag above and this line are what a supervisor needs to
              place a handover: "Concierge · Koktower". This used to render only
              when the SURFACE passed a name, so a manager reading a handover
              from a building other than the one selected in their shell saw no
              building at all. The record now carries its own.

              THE RECORD WINS. The prop was allowed to override it, which made
              every caller holding an ambient "selected building" one stale
              selection away from stamping the wrong desk onto a handover from
              another one. This card is a HISTORICAL SNAPSHOT — the building is
              part of what was true when the shift ended — so the row's own name
              is used and the prop is only a fallback for a record whose building
              could not be resolved server-side. */}
          {h.building_name || buildingName ? (
            <span>{h.building_name || buildingName}</span>
          ) : null}
          <MetaMono>
            {fmtWindow(h.window.from, h.window.to, h.window.tz)}
          </MetaMono>
        </CustodyMeta>
        <CustodyMeta>
          <span>{`Handed over ${clockDayTime(h.created_at, h.window.tz)}`}</span>
          {/* THE SUPPORTING FACTS, in the order they happened, and only where
              they ADD to the status tag rather than restate it.

              A pending handover gets nothing here: "waiting for the next shift"
              is already stated once above, and this line used to say it a second
              time in different words. The two gaps below are the numbers a
              supervisor actually asks for — how long the desk stood empty, and
              how long the record sat unopened after somebody took it. */}
          {/* ── "just now later" ──────────────────────────────────────────
              `elapsed` returns a DURATION for anything over a minute ("4m",
              "1h 20m") and the WORD "just now" under it. Appending "later"
              unconditionally reads correctly for the first form and produces
              "taken over just now later" for the second — which is what the
              screen actually said, on the arrival card, at the exact moment it
              is most likely to be read: the handover is claimed within seconds
              of being written, so the sub-minute branch is the COMMON case here,
              not the edge one.

              The suffix is therefore part of the sentence rather than glued to
              the number. Both readings stay honest: "taken over just now" and
              "taken over 4m later". */}
          {h.claimed_at ? <span>{`· taken over ${gap(h.created_at, h.claimed_at)}`}</span> : null}
          {!h.pending && h.read_at ? (
            <span>{`· opened ${gap(h.created_at, h.read_at)}`}</span>
          ) : null}
          {!h.pending && !h.read_at ? <span>· not opened yet</span> : null}
        </CustodyMeta>
      </CustodyBar>

      <CardBody>
        {/* ── 1. NOT A CLEAN HANDOVER — said before the prose, never after ── */}
        {clamped ? (
          <NoticeBand $tone="warning">
            <BandTitle>The previous shift was never clocked out</BandTitle>
            <span>
              The clock had been running far longer than a shift when this
              handover was made, so nobody closed the one before it. What follows
              covers only the tail of that period — check the log directly for
              anything earlier.
            </span>
          </NoticeBand>
        ) : null}

        {isFloor && reason ? (
          <NoticeBand $tone={reason === 'empty' ? 'neutral' : 'warning'}>
            <BandTitle>No written summary</BandTitle>
            <span>
              {DEGRADED_SENTENCE[reason] ||
                'No written summary was produced for this shift. The facts below come straight from the log.'}
            </span>
          </NoticeBand>
        ) : null}

        {h.status === 'stale' ? (
          <NoticeBand $tone="neutral">
            <BandTitle>Names in this handover have been erased</BandTitle>
            <span>
              Somebody exercised their right to erasure after this handover was
              written. The record is intact; the names inside it are gone for
              good and cannot be restored.
            </span>
          </NoticeBand>
        ) : null}

        {h.tokens_unresolved ? (
          <NoticeBand $tone="neutral">
            <BandTitle>Some names could not be restored</BandTitle>
            <span>
              Parts of this summary still refer to people by a placeholder.
              Open the log for the shift to see who they were.
            </span>
          </NoticeBand>
        ) : null}

        {!h.names_visible && !h.names_erased ? (
          <NoticeBand $tone="neutral">
            <BandTitle>Names are hidden for you</BandTitle>
            <span>
              People are referred to by a placeholder because you do not have
              access to the visitor and daily logs this summary was built from.
            </span>
          </NoticeBand>
        ) : null}

        {/* ── 2. THE SUMMARY ── */}
        <RowHead>
          <Pill tone={statusPill.tone}>{statusPill.text}</Pill>
          {h.status === 'partial' ? (
            <Tag tone="warning">Incident added automatically</Tag>
          ) : null}
        </RowHead>

        {headline ? <Headline>{headline}</Headline> : null}

        {/* A FLOOR RECORD'S SECTIONS ARE FLAGS, NOT A SUMMARY. No model wrote
            this handover, so what the server put here is the short list of
            things somebody must act on — incidents, people with no recorded
            exit, activities left open. Named as that, above the full list that
            now follows it, rather than presented as if it were the account of
            the shift. */}
        {isFloor && sections.length ? <SubHead>Needs attention</SubHead> : null}

        {sections.length ? (
          <SectionList
            sections={sections}
            tz={h.window.tz}
            citations={h.citations}
          />
        ) : isFloor && canSeeLog ? null : (
          <RowText>
            Nothing was logged during this shift, so there was nothing to hand
            over beyond the desk itself.
          </RowText>
        )}

        <Caption>
          {/* The immutability guarantee, said out loud. It is the difference
              between this card and the on-demand summary, and the reader has no
              other way to know which one they are looking at. */}
          This is the handover exactly as it was written when the shift ended. It
          does not change when the log does.
        </Caption>

        {/* ── 3. THE SHIFT, ENTRY BY ENTRY (owner instruction, 2026-09-11) ──
            "Everything, every log in detail." Without IQ the list IS the
            handover, so it is simply there. With IQ the written summary leads
            and the list is one press away — the button the owner asked for.

            ONLY FOR A READER WHO MAY READ THE LOG. The list carries real names,
            and `names_visible` is the server's own answer to "does this reader
            hold the log-read pair" — the same pair the route checks. Anybody
            else would get a 403 dressed as an error box. */}
        {canSeeLog ? (
          isFloor ? (
            <HandoffLog handoffId={h.id} tz={h.window.tz} />
          ) : (
            <>
              <div>
                {/* Not the aurora: opening the raw log is not an AI feature
                    (design_system.md §10). */}
                <GhostButton
                  type="button"
                  aria-expanded={logOpen}
                  onClick={() => setLogOpen(open => !open)}
                >
                  {logOpen ? 'Hide the full log' : 'View the full log'}
                </GhostButton>
              </div>
              {logOpen ? <HandoffLog handoffId={h.id} tz={h.window.tz} /> : null}
            </>
          )
        ) : null}
      </CardBody>
    </IqCard>
  );
};
